import type { DraftRequest, PipelineStage } from '../types';
import { draftText } from '../draft-text';

/**
 * Orders the request so stable content comes first (system prompt, then
 * project-level context, then the conversation summary, then recent turns,
 * then the new user message) and records a CachePlan telling the API client
 * where to place `cache_control: { type: "ephemeral" }` breakpoints so
 * repeated requests against the same file/context version hit Anthropic's
 * prompt cache instead of paying full input price.
 *
 * This stage doesn't remove any content — its "savings" are on cache-read
 * vs. cache-write pricing on *future* requests, not on this request's raw
 * token count, so tokensSaved is always reported as 0 with an explanatory note.
 */
export const d2PromptCachingStructure: PipelineStage = {
  id: 'D2',
  group: 'D',
  label: 'Prompt caching structure',
  description:
    'Order requests with stable content first and mark cache_control breakpoints so repeat requests hit the prompt cache.',
  impact: 'High',
  defaultEnabled: true,
  implemented: true,

  async run(draft, _params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));

    // Stable prefix = every cacheable context block, in the order stages
    // added them (file extracts, dependency stubs, ranked chunks, ...).
    // Non-cacheable blocks (if any future stage produces one — e.g. content
    // that's genuinely different every turn) are left where they are but
    // excluded from the cache plan.
    const cacheableBlocks = draft.contextBlocks.filter((b) => b.cacheable);
    const otherBlocks = draft.contextBlocks.filter((b) => !b.cacheable);
    const reorderedBlocks = [...cacheableBlocks, ...otherBlocks];

    const newDraft: DraftRequest = {
      ...draft,
      contextBlocks: reorderedBlocks,
      cachePlan: {
        stableContextBlockIds: cacheableBlocks.map((b) => b.id),
        summaryIsCacheable: Boolean(draft.historySummary),
      },
    };

    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'D2',
        tokensBefore,
        tokensAfter,
        tokensSaved: 0,
        notes: [
          cacheableBlocks.length > 0
            ? `Marked ${cacheableBlocks.length} context block(s) as a stable, cacheable prefix. Savings show up as cheap cache reads on the next matching request, not on this one.`
            : 'No cacheable context blocks yet — nothing to mark.',
        ],
      },
    };
  },
};
